/**
 * FocusBar — la franja fija del detalle de expediente (paso 2).
 *
 * Responde de un vistazo las tres preguntas con las que un secretario abre
 * un expediente: en cual estoy, cuanto llevo, y que me toca ahora. Hasta
 * ahora las tres se deducian scrolleando nueve modulos, un checklist y un
 * control de estado.
 *
 * Es `sticky` y no `fixed`: se queda pegada arriba del contenido sin sacar
 * al elemento del flujo, asi que no hay que compensar con padding y no se
 * superpone al header del AppShell (que scrollea normal).
 *
 * El titulo NO es un heading. El h1 del documento sigue siendo el titulo
 * editable de la card de abajo; repetirlo como heading crearia dos h1 y
 * romperia el esquema del documento. Aca es chrome de orientacion.
 */

import { ProjectStatusBadge } from "@/components/ProjectStatusBadge";
import type { FormulationDTO } from "@/lib/api/formulations";
import type { ProjectDetailDTO } from "@/lib/api/projects";

import { computeFocusProgress, computeNextAction, type ExpedienteTab } from "./focus";

export function FocusBar({
  project,
  formulation,
  onNavigate,
  cargandoFormulacion = false,
  onReintentarFormulacion,
  conIA = true,
}: {
  project: ProjectDetailDTO;
  /** `null` en expedientes que nacieron de una convocatoria. */
  formulation: FormulationDTO | null;
  /**
   * Lleva a la accion siguiente. Lo resuelve el padre y no la barra porque
   * el destino puede vivir en otra pestana, y con pestanas lo no visible ni
   * siquiera esta montado: hay que cambiar de pestana antes de scrollear.
   */
  onNavigate: (tab: ExpedienteTab, anchorId: string) => void;
  /**
   * La formulacion de un idea-first todavia no llega. Sin ella no hay
   * avance que contar ni siguiente paso que proponer, y adivinarlos seria
   * decirle "Marcar como Radicado" a quien no ha formulado (issue #572).
   */
  cargandoFormulacion?: boolean;
  /**
   * Presente solo si la formulacion de un idea-first no cargo: la barra lo
   * dice y ofrece reintentar (issue #572).
   */
  onReintentarFormulacion?: () => void;
  /** Si el plan trae el asistente IA (ver `computeNextAction`, #629). */
  conIA?: boolean;
}) {
  const progreso = computeFocusProgress(project, formulation);
  const siguiente = computeNextAction(project, formulation, { conIA });
  const pct =
    progreso && progreso.total > 0 ? Math.round((progreso.hechos / progreso.total) * 100) : 0;

  return (
    <div
      data-testid="expediente-focus-bar"
      className="sticky top-0 z-20 -mx-4 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm sm:-mx-6 sm:px-6"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ProjectStatusBadge status={project.status} />
          <p
            data-testid="focus-bar-title"
            title={project.name}
            className="truncate text-sm font-semibold text-slate-900"
          >
            {project.name}
          </p>
        </div>

        {onReintentarFormulacion ? (
          <div className="flex min-w-0 items-center gap-3">
            <p data-testid="focus-bar-error" className="text-sm text-amber-800">
              No pudimos cargar la formulación.
            </p>
            <button
              type="button"
              onClick={onReintentarFormulacion}
              className="min-h-[36px] shrink-0 rounded-lg border border-amber-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-50 focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:outline-hidden"
            >
              Reintentar
            </button>
          </div>
        ) : cargandoFormulacion ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy="true"
            data-testid="focus-bar-cargando"
            className="flex min-w-[160px] flex-1 items-center gap-3 motion-safe:animate-pulse"
          >
            <span className="sr-only">Cargando la formulación…</span>
            <div aria-hidden="true" className="h-1.5 flex-1 rounded-full bg-slate-200" />
            <div aria-hidden="true" className="h-4 w-48 rounded bg-slate-100" />
          </div>
        ) : (
          <>
            {/* Sin nada que contar (issue #569) no hay contador: "0/0" no es avance. */}
            {progreso ? (
              <div
                className="flex min-w-[160px] flex-1 items-center gap-2.5"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={progreso.total}
                aria-valuenow={progreso.hechos}
                aria-valuetext={`${progreso.hechos} de ${progreso.total} ${progreso.etiqueta}`}
              >
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                  <div
                    data-testid="focus-bar-progress-fill"
                    className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="shrink-0 text-xs text-slate-600 tabular-nums">
                  {progreso.hechos}/{progreso.total}{" "}
                  <span className="text-slate-400">{progreso.etiqueta}</span>
                </span>
              </div>
            ) : null}

            {siguiente ? (
              <div className="flex min-w-0 items-center gap-3">
                <p data-testid="focus-bar-next" className="truncate text-sm text-slate-600">
                  Sigue: <span className="font-semibold text-slate-900">{siguiente.label}</span>
                </p>
                <button
                  type="button"
                  data-testid="focus-bar-cta"
                  onClick={() => onNavigate(siguiente.tab, siguiente.targetId)}
                  className="min-h-[36px] shrink-0 rounded-lg bg-cta-500 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-cta-600 focus:ring-2 focus:ring-cta-500 focus:ring-offset-2 focus:outline-hidden"
                >
                  {siguiente.cta}
                </button>
              </div>
            ) : (
              <p data-testid="focus-bar-done" className="text-sm font-medium text-emerald-700">
                Sin pendientes por ahora.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
